import { pb, Project, MarketAnalysis, Persona, Positioning, BusinessModel, MvpPlan, MarketingStrategy, ResearchChat, MentorChat, Payment, PromoCode } from './pocketbase';
import { apiLogger } from './logger';
import { API_URL } from './config';
import { getPhRef } from './phRef';

// Re-export types for convenience
export type { Project, MarketAnalysis, Persona, Positioning, BusinessModel, MvpPlan, MarketingStrategy, ResearchChat, MentorChat, Payment, PromoCode };

// Хелпер для получения ID текущего пользователя
const getUserId = (): string => {
  apiLogger.debug('getUserId called, authStore.isValid:', pb.authStore.isValid);
  apiLogger.debug('authStore.token:', pb.authStore.token ? pb.authStore.token.substring(0, 20) + '...' : 'null');
  apiLogger.debug('authStore.record:', pb.authStore.record);
  if (!pb.authStore.isValid) {
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  const user = pb.authStore.record;
  if (!user?.id) {
    throw new Error('Данные пользователя не загружены. Обновите страницу.');
  }
  apiLogger.debug('userId:', user.id);
  return user.id;
};

// Хелпер для получения токена
const getAuthHeader = (): HeadersInit => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${pb.authStore.token}`,
});

// Безопасный парсинг JSON: если сервер вернул HTML (напр. 504 от nginx) — кидает понятную ошибку
async function parseErrorJson(response: Response): Promise<{ message?: string; error?: string }> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return { message: `Ошибка сервера (${response.status}). Попробуйте ещё раз.` };
}

// Обёртка над fetch: проверяет валидность сессии до запроса и обрабатывает 401
async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  if (!pb.authStore.isValid) {
    pb.authStore.clear();
    window.location.href = '/login';
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  const response = await fetch(url, options);
  if (response.status === 401) {
    pb.authStore.clear();
    window.location.href = '/login';
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }
  return response;
}

// API_URL imported from config.ts

// Хелпер для валидации и экранирования ID (защита от injection)
const validateId = (id: string, fieldName: string = 'id'): string => {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // Pocketbase ID format: 15 символов (alphanumeric)
  const uuidRegex = /^[a-zA-Z0-9]{15}$/;
  if (!uuidRegex.test(id)) {
    throw new Error(`Недопустимое значение ${fieldName}: ${id}`);
  }
  return id;
};

// Безопасный хелпер для создания фильтров
const createFilter = (conditions: Record<string, string>): string => {
  const validated = Object.entries(conditions).map(([key, value]) => {
    const safeValue = validateId(value, key);
    return `${key} = "${safeValue}"`;
  });
  return validated.join(' && ');
};

// ==================== PROJECTS API ====================

export const projectsApi = {
  async list(): Promise<Project[]> {
    // listRule в коллекции фильтрует по user автоматически
    return await pb.collection('projects').getFullList<Project>({
      sort: '-created',
    });
  },

  async getOne(projectId: string): Promise<Project> {
    return await pb.collection('projects').getOne<Project>(projectId);
  },

  async create(data: {
    name: string;
    market?: string;
    stage?: Project['stage'];
    description?: string;
    revenue?: string;
  }): Promise<Project> {
    const response = await apiFetch(`${API_URL}/api/projects`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.message || err.error || 'Failed to create project');
    }
    return response.json();
  },

  async update(projectId: string, data: Partial<Project>): Promise<Project> {
    return await pb.collection('projects').update<Project>(projectId, data);
  },

  async delete(projectId: string): Promise<void> {
    await pb.collection('projects').delete(projectId);
  },

  async setActive(projectId: string, isActive: boolean): Promise<Project> {
    return await pb.collection('projects').update<Project>(projectId, { is_active: isActive });
  },
};

// ==================== MARKET ANALYSES API ====================

export const marketAnalysesApi = {
  async list(projectId?: string): Promise<MarketAnalysis[]> {
    const userId = getUserId();
    const baseFilter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    const filter = `${baseFilter} && status = "completed"`;
    return await pb.collection('market_analyses').getFullList<MarketAnalysis>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<MarketAnalysis> {
    return await pb.collection('market_analyses').getOne<MarketAnalysis>(id);
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      productDescription: string;
      keywords?: string;
      competitors?: string;
      advantage?: string;
      comment?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; pending?: boolean; analysis: MarketAnalysis }> {
    const response = await apiFetch(`${API_URL}/api/ai/market-analysis`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации анализа рынка');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('market_analyses').delete(id);
  },
};

// ==================== PERSONAS API ====================

export const personasApi = {
  async list(projectId?: string): Promise<Persona[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('personas').getFullList<Persona>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<Persona> {
    return await pb.collection('personas').getOne<Persona>(id);
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      marketAnalysisId?: string;
      customPrompt?: string;
    }
  ): Promise<Persona> {
    const response = await apiFetch(`${API_URL}/api/ai/personas`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации персоны');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('personas').delete(id);
  },
};

// ==================== POSITIONINGS API ====================

export const positioningsApi = {
  async list(projectId?: string): Promise<Positioning[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('positionings').getFullList<Positioning>({
      filter,
      sort: 'created',
      expand: 'project,market_analysis,persona',
    });
  },

  async getOne(id: string): Promise<Positioning> {
    return await pb.collection('positionings').getOne<Positioning>(id, {
      expand: 'market_analysis,persona',
    });
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      marketAnalysisId?: string;
      personaId?: string;
    }
  ): Promise<Positioning> {
    const response = await apiFetch(`${API_URL}/api/ai/positioning`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации позиционирования');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('positionings').delete(id);
  },
};

// ==================== BUSINESS MODELS API ====================

export const businessModelsApi = {
  async list(projectId?: string): Promise<BusinessModel[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('business_models').getFullList<BusinessModel>({
      filter,
      sort: 'created',
      expand: 'project,positioning,market_analysis',
    });
  },

  async getOne(id: string): Promise<BusinessModel> {
    return await pb.collection('business_models').getOne<BusinessModel>(id, {
      expand: 'positioning,market_analysis',
    });
  },

  async generate(
    projectId: string,
    data: {
      positioningId?: string;
      marketAnalysisId?: string;
      revenueHint?: string;
      targetMarketSize?: string;
      comment?: string;
    }
  ): Promise<BusinessModel> {
    const response = await apiFetch(`${API_URL}/api/ai/business-model`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации бизнес-модели');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('business_models').delete(id);
  },
};

// ==================== MARKETING STRATEGIES API ====================

export const marketingStrategiesApi = {
  async list(projectId?: string): Promise<MarketingStrategy[]> {
    const userId = getUserId();
    const baseFilter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    const filter = `${baseFilter} && status = "completed"`;
    return await pb.collection('marketing_strategies').getFullList<MarketingStrategy>({
      filter,
      sort: 'created',
      expand: 'project',
    });
  },

  async getOne(id: string): Promise<MarketingStrategy> {
    return await pb.collection('marketing_strategies').getOne<MarketingStrategy>(id, {
      expand: 'project',
    });
  },

  async generate(
    projectId: string,
    data: {
      budget: string;
      mainGoal: string;
      teamSize: string;
      currentChannels?: string;
      marketAnalysisId?: string;
      personaId?: string;
      positioningId?: string;
      comment?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; pending?: boolean; strategy: MarketingStrategy }> {
    const response = await apiFetch(`${API_URL}/api/ai/marketing-strategy`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json() as Record<string, any>;
      const detail = error.detail ? ` (${error.detail})` : '';
      throw new Error((error.message || 'Ошибка генерации маркетинговой стратегии') + detail);
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('marketing_strategies').delete(id);
  },
};

// ==================== MVP PLANS API ====================

export const mvpPlansApi = {
  async list(projectId?: string): Promise<MvpPlan[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('mvp_plans').getFullList<MvpPlan>({
      filter,
      sort: 'created',
      expand: 'project,positioning',
    });
  },

  async getOne(id: string): Promise<MvpPlan> {
    return await pb.collection('mvp_plans').getOne<MvpPlan>(id, {
      expand: 'positioning',
    });
  },

  // AI генерация идёт через Hono backend
  async generate(
    projectId: string,
    data: {
      positioningId?: string;
    }
  ): Promise<MvpPlan> {
    const response = await apiFetch(`${API_URL}/api/ai/mvp-plans`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации MVP плана');
    }

    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('mvp_plans').delete(id);
  },
};

// ==================== RESEARCH CHATS API ====================

export const researchChatsApi = {
  async list(projectId?: string): Promise<ResearchChat[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('research_chats').getFullList<ResearchChat>({
      filter,
      sort: '-updated',
      expand: 'project,persona',
    });
  },

  async getOne(id: string): Promise<ResearchChat> {
    return await pb.collection('research_chats').getOne<ResearchChat>(id, {
      expand: 'persona',
    });
  },

  async create(projectId: string, personaId: string): Promise<ResearchChat> {
    const userId = getUserId();
    return await pb.collection('research_chats').create<ResearchChat>({
      user: userId,
      project: projectId,
      persona: personaId,
      messages: [],
    });
  },

  // Отправка сообщения через Hono backend (AI ответ)
  async sendMessage(
    chatId: string,
    message: string
  ): Promise<{ reply: string; chat: ResearchChat }> {
    const response = await apiFetch(`${API_URL}/api/ai/research-chat`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ chatId, message }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка отправки сообщения');
    }

    return response.json();
  },

  async updateTitle(id: string, title: string): Promise<void> {
    await pb.collection('research_chats').update(id, { title });
  },

  async delete(id: string): Promise<void> {
    await pb.collection('research_chats').delete(id);
  },
};

// ==================== MENTOR CHATS API ====================

export const mentorChatsApi = {
  async list(): Promise<MentorChat[]> {
    const userId = getUserId();
    return await pb.collection('mentor_chats').getFullList<MentorChat>({
      filter: createFilter({ user: userId }),
      sort: '-updated',
    });
  },

  async getOne(id: string): Promise<MentorChat> {
    return await pb.collection('mentor_chats').getOne<MentorChat>(id);
  },

  async create(title?: string): Promise<MentorChat> {
    const userId = getUserId();
    return await pb.collection('mentor_chats').create<MentorChat>({
      user: userId,
      title: title || 'Новый чат',
      messages: [],
      message_count: 0,
    });
  },

  async updateTitle(id: string, title: string): Promise<MentorChat> {
    return await pb.collection('mentor_chats').update<MentorChat>(id, { title });
  },

  // Отправка сообщения через Hono backend (AI ответ)
  async sendMessage(
    chatId: string,
    message: string,
    attachments?: File[],
    artifacts?: Array<{ id: string; type: string }>,
    projectId?: string,
  ): Promise<{ reply: string; artifacts?: Array<{ type: string; recordId: string; status: string; summary?: string }>; chat: MentorChat }> {
    const hasFiles = attachments && attachments.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      formData.append('chatId', chatId);
      formData.append('message', message);
      if (projectId) formData.append('projectId', projectId);
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
      if (artifacts && artifacts.length > 0) {
        formData.append('artifacts', JSON.stringify(artifacts));
      }

      const response = await apiFetch(`${API_URL}/api/ai/mentor-chat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await parseErrorJson(response);
        throw new Error(error.message || 'Ошибка отправки сообщения');
      }
      return response.json();
    }

    const response = await apiFetch(`${API_URL}/api/ai/mentor-chat`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        chatId,
        message,
        ...(projectId ? { projectId } : {}),
        ...(artifacts && artifacts.length > 0 ? { artifacts } : {}),
      }),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || 'Ошибка отправки сообщения');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    await pb.collection('mentor_chats').delete(id);
  },

  // Подсчёт сообщений пользователя (для лимита на бесплатном тарифе)
  async getUserMessageCount(): Promise<number> {
    const userId = getUserId();
    const chats = await pb.collection('mentor_chats').getFullList<MentorChat>({
      filter: createFilter({ user: userId }),
    });
    return chats.reduce((sum, chat) => sum + (chat.message_count || 0), 0);
  },
};

// ==================== CHAT MESSAGES API ====================

export interface ChatMessage {
  id: string;
  chat_id: string;
  chat_type: 'research' | 'mentor';
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
  created: string;
  updated: string;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  page: number;
  perPage: number;
  totalPages: number;
  totalItems: number;
}

export const chatMessagesApi = {
  async list(
    chatId: string,
    chatType: 'research' | 'mentor',
    page = 1,
    perPage = 50
  ): Promise<ChatMessagesResponse> {
    const params = new URLSearchParams({
      chatId,
      chatType,
      page: String(page),
      perPage: String(perPage),
    });

    const response = await apiFetch(`${API_URL}/api/chat-messages?${params}`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Ошибка загрузки сообщений');
    }

    return response.json();
  },
};

// ==================== PAYMENTS API ====================

export const paymentsApi = {
  // Создание платежа через Hono backend (Робокасса)
  async createPayment(data: {
    subscriptionType: 'basic' | 'full' | 'creator' | 'top_creator';
    billingPeriod?: 'monthly' | 'yearly';
    promoCode?: string;
    recurring?: boolean;
    consentGiven?: boolean;
    consentText?: string;
  }): Promise<{ paymentUrl: string; invoiceId: string; amount: number; iframeParams: RobokassaParams }> {
    // Read ph_ref from localStorage first, then fall back to shared cookie
    // (handles cleared localStorage / private mode / DevTools tampering).
    const phRef = getPhRef();
    const response = await apiFetch(`${API_URL}/api/robokassa/create-payment`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ ...data, ...(phRef ? { ph_ref: phRef } : {}) }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка создания платежа');
    }

    return response.json();
  },

  async checkPaymentStatus(invId: string): Promise<{ status: 'pending' | 'success' | 'failed'; subscriptionTier?: string }> {
    const response = await apiFetch(`${API_URL}/api/robokassa/payment-status/${invId}`, {
      headers: getAuthHeader(),
    });
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  },
};

// ==================== PROMO CODES API ====================

export const promoCodesApi = {
  async validate(code: string): Promise<PromoCode | null> {
    const response = await apiFetch(`${API_URL}/api/promo-codes/validate`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  },

  async apply(code: string): Promise<{ success: boolean; message: string }> {
    const response = await apiFetch(`${API_URL}/api/promo-codes/apply`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ code }),
    });

    return response.json();
  },
};

// ==================== FEEDBACK API ====================

export interface FeedbackInput {
  rating: number;
  question_answer?: string;
  comment?: string;
  source?: 'sidebar' | 'proactive';
  page?: string;
}

export const feedbackApi = {
  async submit(data: FeedbackInput): Promise<{ success: boolean; id: string }> {
    const response = await apiFetch(`${API_URL}/api/feedback`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.message || err.error || 'Не удалось отправить отзыв');
    }

    return response.json();
  },
};

// ==================== SUBSCRIPTION API ====================

export interface SubscriptionStatus {
  tier: 'free' | 'basic' | 'full' | 'creator' | 'top_creator';
  isActive: boolean;
  isCancelled: boolean;
  isLegacyOneTime: boolean;
  endDate?: string;
  daysRemaining?: number;
  billingPeriod?: 'monthly' | 'yearly' | null;
  nextBillingDate?: string | null;
}

export const subscriptionApi = {
  async getStatus(): Promise<SubscriptionStatus> {
    if (!pb.authStore.record) {
      throw new Error('Пользователь не авторизован');
    }
    const response = await apiFetch(`${API_URL}/api/subscription/status`, {
      method: 'GET',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка загрузки статуса подписки');
    }

    return response.json();
  },

  async cancel(): Promise<void> {
    const response = await apiFetch(`${API_URL}/api/subscription/cancel`, {
      method: 'POST',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await parseErrorJson(response);
      throw new Error(error.message || error.error || 'Ошибка отмены подписки');
    }
  },
};

// ==================== AI API (unified interface) ====================

export const aiApi = {
  async generateMarketAnalysis(
    projectId: string,
    data: {
      product_description: string;
      keywords?: string;
      competitors?: string;
      advantage?: string;
      comment?: string;
      lang?: string;
    }
  ): Promise<MarketAnalysis> {
    const response = await apiFetch(`${API_URL}/api/ai/market-analysis`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации анализа рынка');
    }

    return response.json();
  },

  async generatePersonas(
    projectId: string,
    data: {
      target_audience?: string;
      market_analysis_id?: string;
      gender?: string;
      age?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; persona: Persona }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации персоны');
    }

    return response.json();
  },

  async updatePersona(
    personaId: string,
    data: {
      target_audience?: string;
      market_analysis_id?: string;
      gender?: string;
      age?: string;
      lang?: string;
    }
  ): Promise<{ success: boolean; persona: Persona }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas/${personaId}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка обновления персоны');
    }

    return response.json();
  },

  async deletePersona(personaId: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${API_URL}/api/ai/personas/${personaId}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка удаления персоны');
    }

    return response.json();
  },

  async generatePositioning(
    projectId: string,
    data: {
      market_analysis_id?: string;
      persona_id?: string;
      product_name?: string;
      unique_value?: string;
      competitors?: string;
      lang?: string;
    }
  ): Promise<Positioning> {
    const response = await apiFetch(`${API_URL}/api/ai/positioning`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации позиционирования');
    }

    return response.json();
  },

  async generateMvpPlan(
    projectId: string,
    data: {
      positioning_id?: string;
      product_description?: string;
      target_features?: string;
      budget?: string;
      timeline?: string;
      tech_stack?: string;
    }
  ): Promise<MvpPlan> {
    const response = await apiFetch(`${API_URL}/api/ai/mvp-plans`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, ...data }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка генерации MVP плана');
    }

    return response.json();
  },

  async researchChat(
    projectId: string,
    personaId: string,
    message: string,
    chatId?: string,
    lang?: string
  ): Promise<{ reply: string; chat: ResearchChat }> {
    const response = await apiFetch(`${API_URL}/api/ai/research-chat`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ projectId, personaId, message, chatId, lang }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка отправки сообщения');
    }

    return response.json();
  },
};

// ==================== FINANCIAL MODELS API ====================

import type { FinancialModelRecord, FinancialModelInputs, MonthlyData, FinancialModelSummary, BusinessModelType } from '@/types/financial-model';

export const financialModelsApi = {
  async list(projectId?: string): Promise<FinancialModelRecord[]> {
    const userId = getUserId();
    const filter = projectId
      ? createFilter({ user: userId, project: projectId })
      : createFilter({ user: userId });
    return await pb.collection('financial_models').getFullList<FinancialModelRecord>({
      filter,
      sort: '-created',
      expand: 'project',
    });
  },

  async create(data: {
    project: string;
    model_name: string;
    model_type: BusinessModelType;
    input_data: FinancialModelInputs;
    calculated_data: { months: MonthlyData[]; summary: FinancialModelSummary };
  }): Promise<FinancialModelRecord> {
    const userId = getUserId();
    return await pb.collection('financial_models').create<FinancialModelRecord>({
      user: userId,
      project: data.project,
      model_name: data.model_name,
      model_type: data.model_type,
      input_data: data.input_data,
      calculated_data: data.calculated_data,
      status: 'draft',
    });
  },

  async update(id: string, data: Partial<FinancialModelRecord>): Promise<FinancialModelRecord> {
    return await pb.collection('financial_models').update<FinancialModelRecord>(id, data);
  },

  async requestAiRecommendations(financialModelId: string): Promise<FinancialModelRecord> {
    const response = await apiFetch(`${API_URL}/api/ai/financial-model`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ financialModelId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Ошибка получения рекомендаций');
    }

    const body = await response.json() as { success: boolean; financialModel: FinancialModelRecord };
    return body.financialModel;
  },

  async delete(id: string): Promise<void> {
    await pb.collection('financial_models').delete(id);
  },
};

// ==================== BUILDER API ====================

// В отличие от остальных артефактов билдер не читается напрямую из PocketBase:
// резервирование адреса, нумерация версий и лимиты требуют серверной проверки,
// поэтому всё идёт через /api/builder — включая чтение, чтобы не держать две тропы.

export type BuildKind = 'landing' | 'deck' | 'app';
export type BuildStatus = 'draft' | 'published' | 'unpublished' | 'suspended';

export interface Build {
  id: string;
  kind: BuildKind;
  name: string;
  slug: string;
  status: BuildStatus;
  project: string | null;
  brief: Record<string, unknown>;
  settings: Record<string, unknown>;
  sourceArtifacts: Record<string, unknown>;
  publishedAt: string | null;
  /** Публичный адрес. null, пока сайт не опубликован. */
  url: string | null;
  /** Сколько заявок пришло (без спама). */
  leads: number;
  created: string;
  updated: string;
}

export interface BuildLead {
  id: string;
  data: Record<string, string>;
  /** utm-метки визита, если были. */
  source: Record<string, string> | null;
  referrer: string | null;
  isSpam: boolean;
  created: string;
}

export interface BuildVersionMeta {
  id: string;
  version: number;
  author: 'ai' | 'user';
  change_summary?: string;
  created: string;
}

/** Схема страницы. Структура описана в backend/src/services/builder/schema.ts. */
export interface PageSchema {
  version: number;
  theme: Record<string, string>;
  seo: { title: string; description: string; ogImage?: string };
  sections: Array<{
    id: string;
    type: string;
    variant: string;
    props: Record<string, unknown>;
    visible: boolean;
  }>;
}

export interface BuilderMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Версия, которую создало это сообщение. null — страница не менялась. */
  version: number | null;
  created: string;
}

/** Операции правки. Определение — backend/src/services/builder/edit-ops.ts. */
export type BuilderOp =
  | { op: 'set_text'; sectionId: string; field: string; value: string }
  | { op: 'replace_section_props'; sectionId: string; props: Record<string, unknown> }
  | { op: 'add_section'; sectionType: string; variant?: string; afterId?: string; props: Record<string, unknown> }
  | { op: 'remove_section'; sectionId: string }
  | { op: 'move_section'; sectionId: string; toIndex: number }
  | { op: 'set_visibility'; sectionId: string; visible: boolean }
  | { op: 'set_theme'; patch: Record<string, unknown> }
  | { op: 'set_seo'; patch: Record<string, unknown> };

async function builderFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await apiFetch(`${API_URL}/api/builder${path}`, {
    headers: getAuthHeader(),
    ...options,
  });

  if (!response.ok) {
    const err = await parseErrorJson(response);
    throw new Error(err.error || err.message || 'Не удалось выполнить запрос');
  }

  return response.json();
}

export const builderApi = {
  /**
   * `publishingEnabled` приходит с сервера, а не выводится из тарифа: пока домен для
   * сайтов не куплен и не настроен, публикация выключена для всех.
   */
  async list(): Promise<{ builds: Build[]; publishingEnabled: boolean }> {
    return builderFetch<{ builds: Build[]; publishingEnabled: boolean }>('');
  },

  async create(data: { name: string; kind?: BuildKind; projectId?: string; brief?: Record<string, unknown> }): Promise<{ build: Build; suggestedSlug: string }> {
    return builderFetch('', { method: 'POST', body: JSON.stringify(data) });
  },

  async get(buildId: string): Promise<{
    build: Build;
    currentVersion: { version: number; author: 'ai' | 'user'; created: string } | null;
    content: PageSchema | null;
    publishingEnabled: boolean;
  }> {
    return builderFetch(`/${buildId}`);
  },

  async update(buildId: string, data: {
    name?: string;
    slug?: string;
    brief?: Record<string, unknown>;
    settings?: Record<string, unknown>;
  }): Promise<Build> {
    const body = await builderFetch<{ build: Build }>(`/${buildId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return body.build;
  },

  async delete(buildId: string): Promise<void> {
    await builderFetch(`/${buildId}`, { method: 'DELETE' });
  },

  async listVersions(buildId: string): Promise<BuildVersionMeta[]> {
    const body = await builderFetch<{ versions: BuildVersionMeta[] }>(`/${buildId}/versions`);
    return body.versions;
  },

  async saveVersion(buildId: string, content: PageSchema, changeSummary?: string): Promise<{ version: number; created: string }> {
    const body = await builderFetch<{ version: { version: number; created: string } }>(
      `/${buildId}/versions`,
      { method: 'POST', body: JSON.stringify({ content, changeSummary }) },
    );
    return body.version;
  },

  /**
   * Применить правки. Одна тропа и для клика по тексту в превью, и для будущих
   * правок через чат — значит и версионирование у них общее.
   */
  async applyOps(buildId: string, ops: BuilderOp[]): Promise<{ version: number; content: PageSchema }> {
    const body = await builderFetch<{ version: { version: number }; content: PageSchema }>(
      `/${buildId}/ops`,
      { method: 'POST', body: JSON.stringify({ ops }) },
    );
    return { version: body.version.version, content: body.content };
  },

  /** Откат добавляет новую версию с прежним содержимым — старая работа не теряется. */
  async restore(buildId: string, version: number): Promise<{ version: number; content: PageSchema }> {
    const body = await builderFetch<{ version: { version: number }; content: PageSchema }>(
      `/${buildId}/restore`,
      { method: 'POST', body: JSON.stringify({ version }) },
    );
    return { version: body.version.version, content: body.content };
  },

  /**
   * Сборка страницы по брифу. Занимает ~30 секунд — нужен индикатор прогресса.
   * `degraded: true` означает, что модель не справилась и вернулась базовая
   * структура по брифу: это стартовая точка, а не готовая страница.
   */
  async generate(buildId: string, brief: Record<string, unknown>, lang?: 'ru' | 'en'): Promise<{
    version: { version: number; created: string };
    content: PageSchema;
    degraded: boolean;
  }> {
    return builderFetch(`/${buildId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ brief, lang }),
    });
  },

  async listMessages(buildId: string): Promise<BuilderMessage[]> {
    const body = await builderFetch<{ messages: BuilderMessage[] }>(`/${buildId}/messages`);
    return body.messages;
  },

  /**
   * Правка обычными словами. `changed: false` означает, что страница осталась
   * прежней — модель задала вопрос или отказалась выдумывать данные.
   */
  async chat(buildId: string, message: string, lang?: 'ru' | 'en'): Promise<{
    reply: string;
    version: number | null;
    content: PageSchema | null;
    changed: boolean;
  }> {
    return builderFetch(`/${buildId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, lang }),
    });
  },

  /** HTML для iframe редактора. Кладётся в srcdoc, поэтому токен не попадает в URL. */
  async preview(buildId: string, version?: number): Promise<{ html: string; version: number }> {
    const query = version ? `?version=${version}` : '';
    return builderFetch(`/${buildId}/preview${query}`);
  },

  async checkSlug(slug: string, buildId?: string): Promise<{ available: boolean; slug?: string; error?: string }> {
    const params = new URLSearchParams({ slug });
    if (buildId) params.set('buildId', buildId);
    return builderFetch(`/slug/check?${params.toString()}`);
  },

  /** Публикация текущей версии. Повторный вызов = перепубликация без простоя. */
  async publish(buildId: string): Promise<{ url: string; version: number }> {
    return builderFetch(`/${buildId}/publish`, { method: 'POST', body: '{}' });
  },

  /** Снятие с публикации. Версии и заявки остаются на месте. */
  async unpublish(buildId: string): Promise<void> {
    await builderFetch(`/${buildId}/unpublish`, { method: 'POST', body: '{}' });
  },

  async listLeads(buildId: string): Promise<BuildLead[]> {
    const body = await builderFetch<{ leads: BuildLead[] }>(`/${buildId}/leads`);
    return body.leads;
  },

  async deleteLead(buildId: string, leadId: string): Promise<void> {
    await builderFetch(`/${buildId}/leads/${leadId}`, { method: 'DELETE' });
  },

  /**
   * Выгрузка заявок в CSV.
   *
   * Файл забирается через fetch с токеном и отдаётся как blob: обычная ссылка ушла бы
   * без заголовка Authorization и вернула 401.
   */
  async downloadLeadsCsv(buildId: string, filename: string): Promise<void> {
    const response = await apiFetch(`${API_URL}/api/builder/${buildId}/leads.csv`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const err = await parseErrorJson(response);
      throw new Error(err.error || 'Не удалось выгрузить заявки');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
};
